import { useMemo } from "react";
import { IVenueBasket } from "@app-contexts/dashboard/features/box-office/basket/interfaces/IVenueBasket";
import VenueBasedInvoiceRepository from "@app-contexts/dashboard/shared/repositories/invoice/VenueBasedInvoiceRepository";
import { VenueBasedInvoiceSerializer } from "@app-contexts/dashboard/shared/types/invoice";
import { IPublicBasket } from "@app-contexts/public/features/basket/interfaces/IPublicBasket";
import PublicBasedInvoiceRepository from "@app-contexts/public/features/invoice-staging/data/repositories/PublicBasedInvoiceRepository";
import { PublicInvoiceDetailSerializer } from "@app-contexts/public/features/invoice-staging/data/types/invoice";
import UserBasedInvoiceRepository from "@app-contexts/user/features/my-orders/data/repositories/UserBasedInvoiceRepository";
import { DataProvider } from "@shared/constants/common";
import { PaymentConfigurationError } from "../utils/errors/PaymentConfigurationError";
import { PaymentSystemError } from "../utils/errors/PaymentSystemError";

/**
 * A service for the PaymentManager to interact with invoices
 */
function useInvoicePaymentService(
  basket: IPublicBasket | IVenueBasket,
  type: DataProvider
) {
  const repository = useMemo(() => {
    let repo;

    switch (type) {
      case DataProvider.Public:
        repo = new PublicBasedInvoiceRepository();
        break;
      case DataProvider.User:
        repo = new UserBasedInvoiceRepository();
        break;
      case DataProvider.Venue:
        repo = new VenueBasedInvoiceRepository({
          venueId: basket.paymentVenue,
        });
        break;
      default:
        throw new PaymentConfigurationError(`Unknown basket type: ${type}`, {
          metadata: { basketType: type },
        });
    }
    return repo;
  }, [type]);

  /**
   * Retrieves a specific Invoice on a domain-specific endpoint.
   * TODO SPW-12069 the heavy usage of casting here is due to mistyped Repositories.
   */
  async function retrieve(
    id: string,
    basketUuids?: {
      uuid: string;
      uuid2: string;
    }
  ) {
    let invoice: VenueBasedInvoiceSerializer | PublicInvoiceDetailSerializer;
    if (repository instanceof VenueBasedInvoiceRepository) {
      const invoiceList = await repository.paginate({ params: { id__in: id } });
      [invoice] = invoiceList.results;
    } else if (repository instanceof PublicBasedInvoiceRepository) {
      if (!basketUuids) {
        throw new PaymentSystemError(
          "Tried to fetch an invoice for a guest with an invalid basket",
          {
            metadata: { invoiceId: id },
            userMessage:
              "There was an issue accessing your invoice. Please try again.",
          }
        );
      }

      const publicInvoice = await repository.list({
        params: {
          uuid1: basketUuids.uuid,
          uuid2: basketUuids.uuid2,
        },
      });

      invoice = Array.isArray(publicInvoice) ? publicInvoice[0] : publicInvoice;
    } else {
      invoice = await repository.retrieve(id);
    }

    return invoice;
  }

  return { retrieve };
}

export default useInvoicePaymentService;
